
import { supabase } from "@/integrations/supabase/client";
import { storageManager } from "@/lib/storageManager";
import { logger } from "@/utils/logger";

export const setupBreedingPhotosStorage = async () => {
  try {
    logger.debug('Setting up breeding photos storage...');
    
    const { data: buckets, error: listError } = await storageManager.getBuckets();
    
    if (listError) {
      logger.error('Error listing buckets:', listError);
      return;
    }

    const breedingPhotosBucket = buckets.find(bucket => bucket.id === 'breeding-photos');
    
    if (!breedingPhotosBucket) {
      logger.debug('Creating breeding-photos bucket...');
      const { error: createError } = await supabase.storage.createBucket('breeding-photos', {
        public: true,
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
        fileSizeLimit: 5242880 // 5MB
      });

      if (createError) {
        logger.error('Error creating bucket:', createError);
      } else {
        logger.debug('Bucket breeding-photos created successfully');
      }
    } else {
      logger.debug('Bucket breeding-photos already exists');
    }
  } catch (error) {
    logger.error('Error setting up storage:', error);
  }
};

export const setupBreederPhotosStorage = async () => {
  try {
    logger.debug('Setting up breeder photos storage...');
    
    const { data: buckets, error: listError } = await storageManager.getBuckets();
    
    if (listError) {
      logger.error('Error listing buckets:', listError);
      return;
    }

    const breederPhotosBucket = buckets.find(bucket => bucket.id === 'breeder-photos');
    
    if (!breederPhotosBucket) {
      logger.debug('Creating breeder-photos bucket...');
      const { error: createError } = await supabase.storage.createBucket('breeder-photos', {
        public: true,
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
        fileSizeLimit: 5242880 // 5MB
      });

      if (createError) {
        logger.error('Error creating bucket:', createError);
      } else {
        logger.debug('Bucket breeder-photos created successfully');
      }
    } else {
      logger.debug('Bucket breeder-photos already exists');
    }
  } catch (error) {
    logger.error('Error setting up storage:', error);
  }
};

export const setupPetPhotosStorage = async () => {
  try {
    logger.debug('Setting up pet photos storage...');
    
    const { data: buckets, error: listError } = await storageManager.getBuckets();
    
    if (listError) {
      logger.error('Error listing buckets:', listError);
      return;
    }

    const petPhotosBucket = buckets.find(bucket => bucket.id === 'pet-photos');
    
    if (!petPhotosBucket) {
      logger.debug('Creating pet-photos bucket...');
      const { error: createError } = await supabase.storage.createBucket('pet-photos', {
        public: true,
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
        fileSizeLimit: 5242880 // 5MB
      });

      if (createError) {
        logger.error('Error creating bucket:', createError);
      } else {
        logger.debug('Bucket pet-photos created successfully');
      }
    } else {
      logger.debug('Bucket pet-photos already exists');
    }
  } catch (error) {
    logger.error('Error setting up storage:', error);
  }
};

export const setupVeterinaryDocumentsStorage = async () => {
  try {
    logger.debug('Setting up veterinary documents storage...');
    
    const { data: buckets, error: listError } = await storageManager.getBuckets();
    
    if (listError) {
      logger.error('Error listing buckets:', listError);
      return;
    }

    const vetDocsBucket = buckets.find(bucket => bucket.id === 'veterinary-documents');
    
    if (!vetDocsBucket) {
      logger.debug('Creating veterinary-documents bucket...');
      const { error: createError } = await supabase.storage.createBucket('veterinary-documents', {
        public: false, // Documents privés
        allowedMimeTypes: [
          'application/pdf',
          'image/jpeg', 
          'image/png', 
          'image/webp',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ],
        fileSizeLimit: 10485760 // 10MB pour les documents
      });

      if (createError) {
        logger.error('Error creating bucket:', createError);
      } else {
        logger.debug('Bucket veterinary-documents created successfully');
      }
    } else {
      logger.debug('Bucket veterinary-documents already exists');
    }
  } catch (error) {
    logger.error('Error setting up storage:', error);
  }
};

export const setupMedicalPhotosStorage = async () => {
  try {
    const { data: buckets, error: listError } = await storageManager.getBuckets();
    if (listError) { logger.error('Error listing buckets:', listError); return; }

    if (!buckets.find(b => b.id === 'medical-photos')) {
      const { error } = await supabase.storage.createBucket('medical-photos', {
        public: false,
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
        fileSizeLimit: 10485760 // 10MB
      });
      if (error) logger.error('Error creating medical-photos bucket:', error);
      else logger.debug('Bucket medical-photos created');
    }
  } catch (error) {
    logger.error('Error setting up medical-photos storage:', error);
  }
};

// Setup all storage buckets needed for the application
export const setupAllStorageBuckets = async () => {
  logger.debug('Initializing all storage buckets...');

  await Promise.all([
    setupBreedingPhotosStorage(),
    setupBreederPhotosStorage(),
    setupPetPhotosStorage(),
    setupVeterinaryDocumentsStorage(),
    setupMedicalPhotosStorage()
  ]);
  
  logger.debug('Storage buckets initialization completed');
};

// Initialize storage on app startup
export const initializeStorage = async () => {
  try {
    await setupAllStorageBuckets();
  } catch (error) {
    logger.error('Failed to initialize storage:', error);
  }
};
